import React, { useEffect, useReducer, useState } from 'react';
import {
    Box,
    Button,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import {
    GetAspNetUsers,
    GetModules,
    GetVendors,
    GetAllPrivileges,
    CreatePrivilege,
    UpdatePrivilege,
    DeletePrivilege,
} from './requests/RadioDesignUserPrivilegesRequests';

import StatsCards          from './components/StatsCards';
import PrivilegesTable     from './components/PrivilegesTable';
import ModuleRulesTable    from './components/ModuleRulesTable';
import PrivilegeDialog     from './components/PrivilegeDialog';
import ConfirmDeleteDialog from './components/ConfirmDeleteDialog';

// ─── Reducer ──────────────────────────────────────────────────────────────────

const initialState = {
    aspNetUsers:       [],
    modules:           [],
    vendors:           [],
    privileges:        [],
    loadingPrivileges: false,
    saving:            false,
    deleting:          false,
    dialogOpen:        false,
    editingPrivilege:  null,
    error:             null,
};

const ACTION = {
    INIT_REQUEST:   'INIT_REQUEST',
    INIT_SUCCESS:   'INIT_SUCCESS',
    INIT_FAILURE:   'INIT_FAILURE',
    SAVE_REQUEST:   'SAVE_REQUEST',
    CREATE_SUCCESS: 'CREATE_SUCCESS',
    UPDATE_SUCCESS: 'UPDATE_SUCCESS',
    SAVE_FAILURE:   'SAVE_FAILURE',
    DELETE_REQUEST: 'DELETE_REQUEST',
    DELETE_SUCCESS: 'DELETE_SUCCESS',
    DELETE_FAILURE: 'DELETE_FAILURE',
    DIALOG_OPEN:    'DIALOG_OPEN',
    DIALOG_CLOSE:   'DIALOG_CLOSE',
    SET_EDITING:    'SET_EDITING',
    CLEAR_ERROR:    'CLEAR_ERROR',
};

const reducer = (state, action) => {
    switch (action.type) {

        case ACTION.INIT_REQUEST:
            return { ...state, loadingPrivileges: true, error: null };
        case ACTION.INIT_SUCCESS:
            return {
                ...state,
                loadingPrivileges: false,
                aspNetUsers: action.payload.aspNetUsers,
                modules:     action.payload.modules,
                vendors:     action.payload.vendors,
                privileges:  action.payload.privileges,
            };
        case ACTION.INIT_FAILURE:
            return { ...state, loadingPrivileges: false, error: action.payload };

        case ACTION.SAVE_REQUEST:
            return { ...state, saving: true, error: null };
        case ACTION.CREATE_SUCCESS:
            return {
                ...state,
                saving:          false,
                dialogOpen:      false,
                editingPrivilege: null,
                privileges:      [...state.privileges, action.payload],
            };
        case ACTION.UPDATE_SUCCESS:
            return {
                ...state,
                saving:          false,
                dialogOpen:      false,
                editingPrivilege: null,
                privileges:      state.privileges.map((p) =>
                    p.userId === action.payload.userId ? action.payload : p
                ),
            };
        case ACTION.SAVE_FAILURE:
            return { ...state, saving: false, error: action.payload };

        case ACTION.DELETE_REQUEST:
            return { ...state, deleting: true, error: null };
        case ACTION.DELETE_SUCCESS:
            return {
                ...state,
                deleting:   false,
                privileges: state.privileges.filter((p) => p.userId !== action.payload),
            };
        case ACTION.DELETE_FAILURE:
            return { ...state, deleting: false, error: action.payload };

        case ACTION.DIALOG_OPEN:
            return { ...state, dialogOpen: true, editingPrivilege: null };
        case ACTION.DIALOG_CLOSE:
            return { ...state, dialogOpen: false, editingPrivilege: null, error: null };
        case ACTION.SET_EDITING:
            return { ...state, dialogOpen: true, editingPrivilege: action.payload };
        case ACTION.CLEAR_ERROR:
            return { ...state, error: null };

        default:
            return state;
    }
};

// ─── Component ────────────────────────────────────────────────────────────────

const RadioDesignUserPrivileges = () => {
    const [state,        dispatch]        = useReducer(reducer, initialState);
    const [tab,          setTab]          = useState(0);
    const [searchText,   setSearchText]   = useState('');
    const [roleFilter,   setRoleFilter]   = useState('');
    const [vendorFilter, setVendorFilter] = useState('');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null, userName: '' });

    const {
        aspNetUsers,
        modules,
        vendors,
        privileges,
        loadingPrivileges,
        saving,
        deleting,
        dialogOpen,
        editingPrivilege,
        error,
    } = state;

    // ── Load all data on mount ─────────────────────────────────
    useEffect(() => {
        const load = async () => {
            dispatch({ type: ACTION.INIT_REQUEST });
            try {
                const [resUsers, resModules, resVendors, resPrivileges] =
                    await Promise.all([GetAspNetUsers(), GetModules(), GetVendors(), GetAllPrivileges()]);

                dispatch({
                    type: ACTION.INIT_SUCCESS,
                    payload: {
                        aspNetUsers: resUsers?.data      ?? [],
                        modules:     resModules?.data    ?? [],
                        vendors:     resVendors?.data     ?? [],
                        privileges:  resPrivileges?.data ?? [],
                    },
                });
            } catch (err) {
                const message =
                    err?.response?.data?.message ||
                    err?.message                 ||
                    'An unexpected error occurred';
                dispatch({ type: ACTION.INIT_FAILURE, payload: message });
            }
        };
        load();
    }, []);

    // ── Save (create or update) ────────────────────────────────
    const handleSave = async (userId, payload, isEdit) => {
        dispatch({ type: ACTION.SAVE_REQUEST });
        try {
            if (isEdit) {
                const { data } = await UpdatePrivilege(userId, payload);
                dispatch({ type: ACTION.UPDATE_SUCCESS, payload: data });
            } else {
                const { data } = await CreatePrivilege(payload);
                dispatch({ type: ACTION.CREATE_SUCCESS, payload: data });
            }
        } catch (err) {
            dispatch({ type: ACTION.SAVE_FAILURE, payload: err?.response?.data?.message || err.message });
        }
    };

    // ── Delete ─────────────────────────────────────────────────
    const handleDeleteConfirm = async () => {
        dispatch({ type: ACTION.DELETE_REQUEST });
        try {
            await DeletePrivilege(deleteDialog.userId);
            dispatch({ type: ACTION.DELETE_SUCCESS, payload: deleteDialog.userId });
            setDeleteDialog({ open: false, userId: null, userName: '' });
        } catch (err) {
            dispatch({ type: ACTION.DELETE_FAILURE, payload: err?.response?.data?.message || err.message });
        }
    };

    const usedUserIds = privileges.map((p) => p.userId);

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>

            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                <Box>
                    <Typography variant="h6" fontWeight={500}>Privilege management</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Assign roles, vendors and module access to existing users
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disableElevation
                    size="small"
                    onClick={() => dispatch({ type: ACTION.DIALOG_OPEN })}
                >
                    Assign privilege
                </Button>
            </Stack>

            {/* Stats */}
            <StatsCards privileges={privileges} vendors={vendors} loading={loadingPrivileges} />

            {/* Tabs */}
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}
            >
                <Tab label="Users & privileges"  sx={{ textTransform: 'none', fontSize: 13 }} />
                <Tab label="Module access rules" sx={{ textTransform: 'none', fontSize: 13 }} />
            </Tabs>

            {/* Tab 0 — Users & privileges */}
            {tab === 0 && (
                <PrivilegesTable
                    privileges={privileges}
                    modules={modules}
                    vendors={vendors}
                    loading={loadingPrivileges}
                    searchText={searchText}
                    roleFilter={roleFilter}
                    vendorFilter={vendorFilter}
                    onSearchChange={setSearchText}
                    onRoleFilterChange={setRoleFilter}
                    onVendorFilterChange={setVendorFilter}
                    onEdit={(p) => dispatch({ type: ACTION.SET_EDITING, payload: p })}
                    onDelete={(p) => setDeleteDialog({ open: true, userId: p.userId, userName: p.userName })}
                />
            )}

            {/* Tab 1 — Module access rules */}
            {tab === 1 && <ModuleRulesTable />}

            {/* Assign / Edit dialog */}
            <PrivilegeDialog
                open={dialogOpen}
                aspNetUsers={aspNetUsers}
                modules={modules}
                vendors={vendors}
                usedUserIds={usedUserIds}
                editingPrivilege={editingPrivilege}
                saving={saving}
                error={error}
                onClose={() => dispatch({ type: ACTION.DIALOG_CLOSE })}
                onSave={handleSave}
                onClearError={() => dispatch({ type: ACTION.CLEAR_ERROR })}
            />

            {/* Delete confirmation */}
            <ConfirmDeleteDialog
                open={deleteDialog.open}
                userName={deleteDialog.userName}
                deleting={deleting}
                onConfirm={handleDeleteConfirm}
                onClose={() => setDeleteDialog({ open: false, userId: null, userName: '' })}
            />
        </Box>
    );
};

export default RadioDesignUserPrivileges;